import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  FileCheck,
  Calculator,
  Scale,
  Shield,
  FileText,
  Users,
  Briefcase,
  ChevronRight,
  ArrowRight,
  Search
} from "lucide-react";
import { navigationData, getAllServices, popularServices } from "@/data/navigationData";

// Icon mapping for service categories
const categoryIcons = {
  registrations: Building2,
  incorporations: Briefcase,
  compliance: FileCheck,
  "tax-gst": Calculator,
  licenses: Shield,
  "intellectual-property": Scale,
  "legal-documents": FileText,
  disputes: Scale,
  "cxo-services": Users,
};

// Filter only service-related menu items (exclude Resources)
const serviceMenus = navigationData.filter(menu => menu.id !== "resources");

export default function ServicesPage() {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredServices, setFilteredServices] = useState([]);
  const allServices = getAllServices();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      }
    }
  }, [location]);

  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const results = allServices.filter(
        service =>
          service.label.toLowerCase().includes(query) ||
          service.description?.toLowerCase().includes(query) ||
          service.menuLabel.toLowerCase().includes(query)
      );
      setFilteredServices(results);
    } else {
      setFilteredServices([]);
    }
  }, [searchQuery, allServices]);

  // Get popular service details
  const popularServiceDetails = popularServices
    .map(id => allServices.find(s => s.id === id))
    .filter(Boolean);

  return (
    <main data-testid="services-page" className="pt-24 md:pt-32 bg-[#050505]">
      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-12 md:pb-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-xs font-mono tracking-wider uppercase text-[#00C896]/80 mb-4 block">
            Legal & Business Services
          </span>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            Our Services
          </h1>
          <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-3xl mb-8">
            Comprehensive legal, tax, and compliance solutions for businesses of all sizes.
            From company registration to intellectual property protection, we handle everything
            so you can focus on growing your business.
          </p>

          {/* Search Box */}
          <div className="relative max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search services... (e.g., GST registration, trademark)"
              className="w-full pl-12 pr-4 py-4 rounded-xl bg-[#0A0A0A] border border-white/10 text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00C896]/50 transition-colors"
            />

            {/* Search Results Dropdown */}
            {filteredServices.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute top-full left-0 right-0 mt-2 bg-[#0A0A0A] border border-white/10 rounded-xl shadow-2xl max-h-80 overflow-y-auto z-20"
              >
                {filteredServices.slice(0, 8).map((service) => (
                  <Link
                    key={service.id}
                    to={service.path}
                    className="flex items-center justify-between p-4 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                    onClick={() => setSearchQuery("")}
                  >
                    <div>
                      <p className="text-white font-medium">{service.label}</p>
                      <p className="text-xs text-slate-500">{service.menuLabel} → {service.categoryLabel}</p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-500" />
                  </Link>
                ))}
              </motion.div>
            )}
          </div>
        </motion.div>
      </section>

      {/* Popular Services */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <h2 className="text-sm font-mono tracking-wider uppercase text-slate-500 mb-6">
            Popular Services
          </h2>
          <div className="flex flex-wrap gap-3">
            {popularServiceDetails.map((service) => (
              <Link
                key={service.id}
                to={service.path}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#00C896]/10 border border-[#00C896]/20 text-[#00C896] text-sm font-medium hover:bg-[#00C896]/20 transition-colors"
              >
                {service.label}
                <ArrowRight size={14} />
              </Link>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Service Categories */}
      <section className="border-t border-white/5">
        {serviceMenus.map((menu, menuIndex) => {
          const IconComponent = categoryIcons[menu.id] || FileText;

          return (
            <motion.div
              key={menu.id}
              id={menu.id}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6 }}
              className={`border-b border-white/5 ${menuIndex % 2 === 0 ? "bg-[#050505]" : "bg-[#0A0A0A]"
                }`}
            >
              <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
                {/* Category Header */}
                <div className="flex items-start gap-6 mb-10">
                  <div className="w-14 h-14 rounded-2xl bg-[#00C896]/10 border border-[#00C896]/20 flex items-center justify-center shrink-0">
                    <IconComponent className="w-7 h-7 text-[#00C896]" />
                  </div>
                  <div>
                    <h2
                      className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-2"
                      style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                    >
                      {menu.label}
                    </h2>
                    <p className="text-slate-400">
                      {menu.categories?.length} categories • {menu.categories?.reduce((acc, cat) => acc + (cat.services?.length || 0), 0)} services
                    </p>
                  </div>
                </div>

                {/* Subcategories */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {menu.categories?.map((category) => (
                    <div key={category.id} className="space-y-4">
                      <h3 className="text-sm font-mono tracking-wider uppercase text-[#00C896]/80">
                        {category.label}
                      </h3>
                      <div className="space-y-2">
                        {category.services?.map((service) => (
                          <Link
                            key={service.id}
                            to={service.path}
                            data-testid={`service-link-${service.id}`}
                            className="group flex items-center justify-between p-4 rounded-xl bg-white/[0.02] border border-white/5 hover:border-[#00C896]/20 hover:bg-[#00C896]/5 transition-all"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-white font-medium group-hover:text-[#00C896] transition-colors truncate">
                                {service.label}
                              </p>
                              <p className="text-xs text-slate-500 truncate mt-1">
                                {service.description}
                              </p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-[#00C896] shrink-0 ml-4 transition-colors" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          );
        })}
      </section>

      {/* Stats Section */}
      <section className="border-t border-white/5 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-[#00C896] mb-2">50,000+</p>
              <p className="text-slate-400">Businesses Served</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-[#00C896] mb-2">98%</p>
              <p className="text-slate-400">Success Rate</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-[#00C896] mb-2">10+</p>
              <p className="text-slate-400">Years Experience</p>
            </div>
            <div className="text-center">
              <p className="text-4xl md:text-5xl font-bold text-[#00C896] mb-2">4.9/5</p>
              <p className="text-slate-400">Client Rating</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2
              className="text-3xl md:text-4xl font-semibold tracking-tight text-white mb-6"
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Not Sure Which Service You Need?
            </h2>
            <p className="text-lg text-slate-400 leading-relaxed mb-10">
              Our experts are here to help. Book a free consultation and get personalized
              recommendations based on your business requirements.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="services-page-cta"
                className="inline-flex items-center gap-2 bg-[#00C896] text-black font-bold px-10 py-4 rounded-full hover:bg-[#00E6AC] transition-colors shadow-[0_0_20px_rgba(0,200,150,0.3)] hover:shadow-[0_0_30px_rgba(0,200,150,0.5)]"
              >
                Schedule Free Consultation
                <ArrowRight size={18} />
              </Link>
              <a
                href="tel:+919899462024"
                className="inline-flex items-center gap-2 border border-white/20 text-white font-medium px-10 py-4 rounded-full hover:bg-white/5 transition-colors"
              >
                Call: +91 9899462024
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
